import { useQuery } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'

// Type exports for convenience
export type WebhookResponse = components['schemas']['WebhookResponse']
export type EventTypesListResponse =
  components['schemas']['EventTypesListResponse']
export type WebhookDeliveryResponse =
  components['schemas']['WebhookDeliveryResponse']

// Get all available event types
export function useWebhookEventTypes() {
  return useQuery({
    queryKey: queryKeys.webhooks.events,
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/webhooks/events')
      if (error) throw error
      return data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - event types rarely change
  })
}

// List all webhooks
export function useWebhooks(params?: { skip?: number; limit?: number }) {
  const skip = params?.skip ?? 0
  const limit = params?.limit ?? 100

  return useQuery({
    queryKey: queryKeys.webhooks.list(skip, limit),
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/webhooks', {
        params: { query: { skip, limit } },
      })
      if (error) throw error
      return data
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Get webhook details
export function useWebhook(webhookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.webhooks.detail(String(webhookId!)),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/webhooks/{webhook_id}',
        {
          params: { path: { webhook_id: String(webhookId!) } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!webhookId,
    staleTime: 30 * 1000,
  })
}

// List webhook deliveries
export function useWebhookDeliveries(
  webhookId: number | undefined,
  params?: { skip?: number; limit?: number },
) {
  const skip = params?.skip ?? 0
  const limit = params?.limit ?? 100

  return useQuery({
    queryKey: queryKeys.webhooks.deliveries(String(webhookId!), skip, limit),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/webhooks/{webhook_id}/deliveries',
        {
          params: {
            path: { webhook_id: String(webhookId!) },
            query: { skip, limit },
          },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!webhookId,
    staleTime: 10 * 1000, // 10 seconds - delivery status changes frequently
  })
}

// Get delivery details
export function useWebhookDelivery(deliveryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.webhooks.delivery(deliveryId!),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/webhooks/deliveries/{delivery_id}',
        {
          params: { path: { delivery_id: deliveryId! } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!deliveryId,
    staleTime: 60 * 1000, // 1 minute
  })
}
